import moment from "moment";
import { useRef, useState } from "react";
import api from "../../services/api";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/useAuth";
import { defaultDateFormat } from "../../utils/utils";
import { termsToPdf } from "../../utils/exportPDFutils";
import TermsOfUseText from "../layout/TermsOfUseText";
import { useTranslation } from "react-i18next";

function ModalProfileTermsOfUse({ setProfile, profile, showSign = true }) {
  const { termsOfUseVersion, brand, auth } = useAuth();
  const { t } = useTranslation();
  // Defended lookup: `?? termsOfUseVersion.default` guarantees a defined version
  // even if `brand` is ever an unknown key (which would otherwise resolve to
  // `undefined` and break both the write and the comparisons below).
  const currentTermsVersion = termsOfUseVersion[brand || "default"] ?? termsOfUseVersion.default;
  const [isExporting, setIsExporting] = useState(false);
  const termsContentRef = useRef(null);

  const fullName = auth?.user ? auth.user.firstName + " " + auth.user.lastName : "";
  // "Signed" requires the version stamp too — a half-signed profile (date saved,
  // version dropped by the old junk-brand bug) must not render the signed badge
  // (it would show version "undefined" and sit beside the red not-signed badge).
  // `!= null` (not === current): an OLDER version is still a valid past signature;
  // the re-sign affordances below handle outdated versions.
  const isSigned = profile.custom?.doctorConsentAuthorizeSignatureDate && profile.custom?.doctorConsentAuthorizeVersion != null;

  const handleExportPdf = async () => {
    if (!termsContentRef.current) return;
    setIsExporting(true);
    try {
      await termsToPdf({
        contentElement: termsContentRef.current,
        signedDate: profile.custom?.doctorConsentAuthorizeSignatureDate,
        signedVersion: profile.custom?.doctorConsentAuthorizeVersion,
        signerName: fullName,
        signerEmail: auth?.user?.email,
        signerTitle: auth?.user?.custom?.title || auth?.user?.title || auth?.user?.jobTitle,
        organizationName: auth?.orgInfo?.officeName,
        // Phase 2 will populate doctorConsentAuthorizeAudit with consentTs +
        // deviceMetadata + pin/photo attestations. Until then the certificate
        // renders with the basic fields only and degrades gracefully.
        auditRecord: profile.custom?.doctorConsentAuthorizeAudit,
      });
    } catch (error) {
      console.error("Error exporting Terms of Use to PDF:", error);
      toast.error(t("profile.toasts.pdfExportError"));
    } finally {
      setIsExporting(false);
    }
  };

  const sign = async () => {
    let updatedProfile = {
      custom: {
        doctorConsentAuthorizeVersion: currentTermsVersion,
        doctorConsentAuthorizeSignature: profile.firstName + " " + profile.lastName,
        doctorConsentAuthorizeSignatureDate: new Date().toISOString(),
      },
    };

    try {
      let res = await api.updateProfile(updatedProfile);

      if (res.status === 200 || res.status === 204) {
        toast.info(t("terms.toast.signed"));
        setProfile({ ...profile, ...updatedProfile });
      } else {
        toast.error(t("terms.toast.signError"));
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || t("terms.toast.signError"));
    }
  };

  return (
    <>
      <div className="term-of-use-content" ref={termsContentRef}>
        <TermsOfUseText />

        {isSigned && (
          <div className="mt-50 vertical-align badge green-dark pdf-hide">
            <i className="material-symbols-outlined mr-10">signature</i>
            {t("terms.signedBadge", {
              version: profile.custom.doctorConsentAuthorizeVersion,
              date: moment(profile.custom?.doctorConsentAuthorizeSignatureDate).format(defaultDateFormat),
              name: profile.custom.doctorConsentAuthorizeSignature,
            })}
          </div>
        )}

        {(!profile.custom || profile.custom?.doctorConsentAuthorizeVersion !== currentTermsVersion) && (
          <div className="sign-section mt-50 badge red">{t("terms.notSigned")}</div>
        )}
      </div>

      {showSign && (
        <div className="modal-actions">
          {isSigned && (
            <button className="btn btn-secondary btn-small" onClick={handleExportPdf} disabled={isExporting}>
              <i className="material-symbols-outlined">download</i>{" "}
              {isExporting ? t("profile.baa.exporting") : t("profile.baa.download")}
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={sign}
            disabled={profile.custom?.doctorConsentAuthorizeVersion === currentTermsVersion}
          >
            {t("terms.sign")}
          </button>
        </div>
      )}
    </>
  );
}

export default ModalProfileTermsOfUse;
